import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  Sheet,
  CircularProgress,
  IconButton,
  Drawer,
  Button,
  FormControl,
  Input,
  Select,
  Option,
} from "@mui/joy";
import { useQuery, gql, useMutation } from "@apollo/client";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import Menu from "@mui/joy/Menu";
import MenuItem from "@mui/joy/MenuItem";
import MenuButton from "@mui/joy/MenuButton";
import Dropdown from "@mui/joy/Dropdown";
import { getUserProfile } from "../jwt";
import Pagination from "./Pagination";
import { usePagination } from "./usePagination";

const UNIQUE_ASSETS_QUERY = gql`
  query UniqueAssets(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filters: AssetFilter
  ) {
    uniqueAssets(
      first: $first
      after: $after
      last: $last
      before: $before
      filters: $filters
    ) {
      edges {
        node {
          id
          make
          model
          type
          category
          feeClass
          tare
          gvm
          description
          plateNumber
          regNumber
          identificationNumber
          expiryDate
          status
          province
          createdAt
          owner {
            id
            firstName
            lastName
            email
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

const EDIT_ASSET_TYPE_MUTATION = gql`
  mutation EditAssetType(
    $id: ID!
    $category: String!
    $feeClass: String!
    $tare: Int!
    $gvm: Int!
  ) {
    editAssetType(
      input: {
        id: $id
        category: $category
        feeClass: $feeClass
        tare: $tare
        gvm: $gvm
      }
    ) {
      asset {
        id
        category
        feeClass
        tare
        gvm
      }
      updatedCount
    }
  }
`;

const getCategoryDisplayLabel = (categoryCode: string) => {
  const categories = [
    { code: "BK", label: "BK - Motorcycles / Quadrocycles / Tricycles" },
    { code: "CR", label: "CR - Car / Station Wagon" },
    { code: "MB", label: "MB - Minibusses" },
    { code: "BU", label: "BU - Bus / Bus Train / Midibusses" },
    { code: "LD", label: "LD - LDV / Panel Van / Other light load vehicles" },
    { code: "TK", label: "TK - Truck" },
    { code: "CV", label: "CV - Caravan" },
    { code: "LT", label: "LT - Light Load Trailer" },
    { code: "HT", label: "HT - Heavy Load Trailer" },
    { code: "OV", label: "OV - Other Self Propelled" },
  ];

  const category = categories.find((cat) => cat.code === categoryCode);
  return category ? category.label : categoryCode;
};

// Category options for the dropdown
const CATEGORY_OPTIONS = [
  { value: "BK", label: "BK - Motorcycles / Quadrocycles / Tricycles" },
  { value: "CR", label: "CR - Car / Station Wagon" },
  { value: "MB", label: "MB - Minibusses" },
  { value: "BU", label: "BU - Bus / Bus Train / Midibusses" },
  { value: "LD", label: "LD - LDV / Panel Van / Other light load vehicles" },
  { value: "TK", label: "TK - Truck" },
  { value: "CV", label: "CV - Caravan" },
  { value: "LT", label: "LT - Light Load Trailer" },
  { value: "HT", label: "HT - Heavy Load Trailer" },
  { value: "OV", label: "OV - Other Self Propelled" },
];

const Row = (props: { row: any; key?: any }) => {
  const { row } = props;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(row.category || "");
  const [feeClass, setFeeClass] = useState(row.feeClass || "");
  const [tare, setTare] = useState(row.tare || "");
  const [gvm, setGvm] = useState(row.gvm || "");
  const [tareError, setTareError] = useState("");
  const [gvmError, setGvmError] = useState("");
  const [selectOpen, setSelectOpen] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const [editAssetType] = useMutation(EDIT_ASSET_TYPE_MUTATION, {
    refetchQueries: [{ query: UNIQUE_ASSETS_QUERY }],
  });

  const handleEdit = () => {
    setSelectedCategory(row.category || CATEGORY_OPTIONS[1].value);
    setFeeClass(row.feeClass || "");
    setTare(row.tare || "");
    setGvm(row.gvm || "");
    setTareError("");
    setGvmError("");
    setErrors([]);
    setDrawerOpen(true);
  };

  const isFormValid =
    selectedCategory && feeClass && tare && gvm && !tareError && !gvmError;

  const handleSave = async () => {
    setErrors([]);
    setIsSaving(true);

    try {
      const result = await editAssetType({
        variables: {
          id: row.id,
          category: selectedCategory,
          feeClass: feeClass,
          tare: parseInt(tare),
          gvm: parseInt(gvm),
        },
      });

      setDrawerOpen(false);

      // Show success message with count of updated assets
      if (result.data?.editAssetType?.updatedCount) {
        console.log(
          `Successfully updated ${result.data.editAssetType.updatedCount} assets`
        );
      }
    } catch (error: any) {
      setErrors([error.message || "Failed to edit asset type"]);
    } finally {
      setIsSaving(false);
    }
  };

  const validateTare = (value: string) => {
    if (!value) return "Tare is required";
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue <= 0)
      return "Tare must be a positive number";
    return "";
  };

  const validateGvm = (value: string) => {
    if (!value) return "GVM is required";
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 0)
      return "GVM must be a non-negative number";
    return "";
  };

  return (
    <>
      <tr>
        <td>
          <Typography level="body-xs">{row.make}</Typography>
        </td>
        <td>
          <Typography level="body-xs">{row.model}</Typography>
        </td>
        <td>
          <Typography level="body-xs">{row.type}</Typography>
        </td>
        <td>
          <Typography level="body-xs">
            {getCategoryDisplayLabel(row.category)}
          </Typography>
        </td>
        <td>
          <Typography level="body-xs">{row.feeClass}</Typography>
        </td>
        <td>
          <Typography level="body-xs">
            {row.tare ? `${row.tare} kg` : "-"}
          </Typography>
        </td>
        <td>
          <Typography level="body-xs">
            {row.gvm ? `${row.gvm} kg` : "-"}
          </Typography>
        </td>
        <td>
          <Dropdown>
            <MenuButton
              slots={{ root: IconButton }}
              slotProps={{
                root: {
                  variant: "plain",
                  color: "neutral",
                  size: "sm",
                  sx: {
                    borderRadius: "8px",
                    "&:hover": {
                      backgroundColor: "background.level1",
                    },
                  },
                },
              }}
            >
              <MoreHorizIcon fontSize="small" />
            </MenuButton>
            <Menu size="sm" sx={{ minWidth: 140 }}>
              <MenuItem onClick={handleEdit}>Edit</MenuItem>
            </Menu>
          </Dropdown>
        </td>
      </tr>

      {/* Edit Drawer */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        anchor="right"
        size="md"
        sx={{
          "& .MuiDrawer-paper": {
            width: "600px",
            p: 0,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "background.surface",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.level1",
            }}
          >
            <Typography level="h4" sx={{ mb: 1, fontWeight: "bold" }}>
              Edit Asset
            </Typography>
            <Typography level="body-sm" color="neutral">
              {row.make} {row.model} • {row.type}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
            {/* Error Messages */}
            {errors.length > 0 && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: "danger.50",
                  borderRadius: 2,
                }}
              >
                <Typography level="body-sm" color="danger">
                  {errors.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </Typography>
              </Box>
            )}

            {/* Category Dropdown */}
            <FormControl sx={{ mb: 4, width: "100%" }}>
              <Typography
                level="body-sm"
                sx={{ mb: 2, fontWeight: "600", color: "text.primary" }}
              >
                Category *
              </Typography>
              <Select
                value={selectedCategory}
                open={selectOpen}
                onChange={(event, newValue) => {
                  setSelectedCategory(newValue || "");
                  setSelectOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  "& .MuiSelect-select": {
                    py: 1.5,
                  },
                }}
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </FormControl>

            {/* Fees Class */}
            <FormControl sx={{ mb: 4, width: "100%" }}>
              <Typography
                level="body-sm"
                sx={{ mb: 2, fontWeight: "600", color: "text.primary" }}
              >
                Fees Class *
              </Typography>
              <Input
                value={feeClass}
                onChange={(e) => setFeeClass(e.target.value)}
                placeholder="Enter fees class"
                sx={{
                  borderRadius: 2,
                  "& .MuiInput-input": {
                    py: 1.5,
                  },
                }}
              />
            </FormControl>

            {/* GVM */}
            <FormControl sx={{ mb: 4, width: "100%" }}>
              <Typography
                level="body-sm"
                sx={{ mb: 2, fontWeight: "600", color: "text.primary" }}
              >
                GVM *
              </Typography>
              <Input
                type="number"
                value={gvm}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^[0-9]\d*$/.test(value)) {
                    setGvm(value);
                    setGvmError(validateGvm(value));
                  }
                }}
                placeholder="Enter GVM"
                color={gvmError ? "danger" : "neutral"}
                sx={{
                  borderRadius: 2,
                  "& .MuiInput-input": {
                    py: 1.5,
                  },
                }}
              />
              {gvmError && (
                <Typography level="body-xs" color="danger" sx={{ mt: 1 }}>
                  {gvmError}
                </Typography>
              )}
            </FormControl>

            {/* Tare */}
            <FormControl sx={{ mb: 4, width: "100%" }}>
              <Typography
                level="body-sm"
                sx={{ mb: 2, fontWeight: "600", color: "text.primary" }}
              >
                Tare *
              </Typography>
              <Input
                type="number"
                value={tare}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^[1-9]\d*$/.test(value)) {
                    setTare(value);
                    setTareError(validateTare(value));
                  }
                }}
                placeholder="Enter tare"
                color={tareError ? "danger" : "neutral"}
                sx={{
                  borderRadius: 2,
                  "& .MuiInput-input": {
                    py: 1.5,
                  },
                }}
              />
              {tareError && (
                <Typography level="body-xs" color="danger" sx={{ mt: 1 }}>
                  {tareError}
                </Typography>
              )}
            </FormControl>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              p: 3,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.level1",
            }}
          >
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => setDrawerOpen(false)}
              disabled={isSaving}
              sx={{
                flex: 1,
                borderRadius: 2,
                py: 1.5,
                fontWeight: "600",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={handleSave}
              disabled={isSaving || !isFormValid}
              sx={{
                flex: 1,
                borderRadius: 2,
                py: 1.5,
                fontWeight: "600",
              }}
            >
              {isSaving ? "Saving..." : "Edit Asset Type"}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

// Search inputs component with debouncing
const AssetTypeSearch = () => {
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [type, setType] = useState("");

  const [debouncedMake, setDebouncedMake] = useState("");
  const [debouncedModel, setDebouncedModel] = useState("");
  const [debouncedType, setDebouncedType] = useState("");

  // Debounce search inputs
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMake(make);
    }, 300);
    return () => clearTimeout(timer);
  }, [make]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedModel(model);
    }, 300);
    return () => clearTimeout(timer);
  }, [model]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedType(type);
    }, 300);
    return () => clearTimeout(timer);
  }, [type]);

  return (
    <>
      {/* Search Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mb: 3,
          width: "100%",
        }}
      >
        {/* Search Row */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <Typography level="body-sm" sx={{ mb: 0.5 }}>
              Make
            </Typography>
            <Input
              placeholder="Search by make..."
              startDecorator={<SearchIcon />}
              endDecorator={
                make && (
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setMake("")}
                  >
                    <ClearIcon />
                  </IconButton>
                )
              }
              value={make}
              onChange={(event) => setMake(event.target.value)}
            />
          </FormControl>

          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <Typography level="body-sm" sx={{ mb: 0.5 }}>
              Model
            </Typography>
            <Input
              placeholder="Search by model..."
              startDecorator={<SearchIcon />}
              endDecorator={
                model && (
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setModel("")}
                  >
                    <ClearIcon />
                  </IconButton>
                )
              }
              value={model}
              onChange={(event) => setModel(event.target.value)}
            />
          </FormControl>

          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <Typography level="body-sm" sx={{ mb: 0.5 }}>
              Type
            </Typography>
            <Input
              placeholder="Search by type..."
              startDecorator={<SearchIcon />}
              endDecorator={
                type && (
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setType("")}
                  >
                    <ClearIcon />
                  </IconButton>
                )
              }
              value={type}
              onChange={(event) => setType(event.target.value)}
            />
          </FormControl>
        </Box>
      </Box>

      <AssetTypeTable
        make={debouncedMake}
        model={debouncedModel}
        type={debouncedType}
      />
    </>
  );
};

// Table component that receives debounced search values as props
const AssetTypeTable = ({
  make,
  model,
  type,
}: {
  make: string;
  model: string;
  type: string;
}) => {
  const [pageSize] = useState(10);

  const { data, loading, error, fetchMore } = useQuery(UNIQUE_ASSETS_QUERY, {
    variables: {
      first: pageSize,
      filters: {
        make: make || undefined,
        model: model || undefined,
        type: type || undefined,
      },
    },
  });

  const {
    pageInfo,
    isLoadingMore,
    handlePageChange,
    canGoNext,
    canGoPrevious,
  } = usePagination({
    pageSize,
    data,
    fetchMore,
    getPageInfo: (data) => data.uniqueAssets.pageInfo,
    updateQuery: (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult) return prev;
      return {
        uniqueAssets: {
          ...fetchMoreResult.uniqueAssets,
          edges: [...fetchMoreResult.uniqueAssets.edges],
        },
      };
    },
  });

  if (loading && !data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Typography level="body-lg" color="danger">
          Error loading unique assets: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th style={{ width: "15%", padding: "12px 6px" }}>Make</th>
              <th style={{ width: "15%", padding: "12px 6px" }}>Model</th>
              <th style={{ width: "15%", padding: "12px 6px" }}>Type</th>
              <th style={{ width: "15%", padding: "12px 6px" }}>Category</th>
              <th style={{ width: "12%", padding: "12px 6px" }}>Fee Class</th>
              <th style={{ width: "10%", padding: "12px 6px" }}>Tare</th>
              <th style={{ width: "10%", padding: "12px 6px" }}>GVM</th>
              <th style={{ width: "8%", padding: "12px 6px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading || isLoadingMore ? (
              <tr>
                <td colSpan={8}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 2,
                    }}
                  >
                    <CircularProgress size="sm" />
                  </Box>
                </td>
              </tr>
            ) : data?.uniqueAssets?.edges?.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 4,
                    }}
                  >
                    <Typography level="body-lg" color="neutral">
                      No unique assets found.
                    </Typography>
                  </Box>
                </td>
              </tr>
            ) : (
              data?.uniqueAssets?.edges?.map(({ node: row }) => (
                <Row key={row.id} row={row} />
              ))
            )}
          </tbody>
        </Table>
      </Sheet>
      <Pagination
        pageInfo={pageInfo}
        isLoadingMore={isLoadingMore}
        onPageChange={handlePageChange}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
      />
    </>
  );
};

const AssetType = () => {
  // Check if user is super user
  const currentUserProfile = getUserProfile();
  const isSuperUser = currentUserProfile?.currentRole === "SUPER_USER";

  // If not super user, show access denied message
  if (!isSuperUser) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          textAlign: "center",
          p: 4,
        }}
      >
        <Typography level="h3" color="danger" sx={{ mb: 2 }}>
          Access Denied
        </Typography>
        <Typography level="body-lg" color="neutral">
          You don't have permission to access this page. Only Super Users can
          manage Asset Types.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        mb: 1,
        gap: 1,
        flexDirection: "column",
        flexWrap: "wrap",
        justifyContent: "space-between",
      }}
    >
      <AssetTypeSearch />
    </Box>
  );
};

export default AssetType;
